'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';
import { getAsset, getAssetForWidth } from '@/lib/assets';
import { useManifest } from '@/lib/manifest-context';

import BannerFeatureCards from '../BannerServicePages/BannerFeatureCards';

import HeroBadge from './components/HeroBadge';
import HeroCollage from './components/HeroCollage';
import HeroDescription from './components/HeroDescription';
import HeroGradientBlobs from './components/HeroGradientBlobs';
import type { ServiceHeroSectionData } from './ServiceHeroSection.types';

import type { ButtonBlock } from '@/types/pageContentTypes';
import BaseHeroSection, {
  TrustLogoItem,
} from '@/components/sections/BaseHeroSection/BaseHeroSection';
import {
  ClinicDoctifyWidget,
  ClinicTrustPilotWidget,
} from '@/components/widgets/ClinicDoctifyWidget';

interface ServiceHeroSectionProps {
  data?: ServiceHeroSectionData;
  onButtonClick?: (
    button: ButtonBlock,
    index: number,
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
}

const BOOKING_WIDGET_TARGET_ID = 'booking-calendar-widget';

const ENQUIRY_FORM_TARGET_IDS = [
  'claim-form',
  'reserve-form',
  'service-enquiry-form',
  'enquiry-form',
  'enquire-form',
  'enquire-now-form',
  'serviceEnquiry',
  'pharmacist_advices_treatment_section',
  'love-day-form',
];

const BOOKING_FALLBACK_PATH = '/specialities?filter=all';
const BOOKING_SCROLL_OFFSET = 112;

function getDefaultTrustLogos(
  manifest: ReturnType<typeof useManifest>,
): TrustLogoItem[] {
  return [
    {
      type: 'trustpilot',
      scale: 'scale-[0.6] md:scale-[0.6]',
    },
    {
      type: 'image',
      content: `[![Top Doctors](${getAsset(
        manifest,
        '/images/top-doctors-logo.png',
      )} "160x160")](https://www.topdoctors.co.uk/gh/lips-healthcare/)`,
      scale: 'scale-[0.9] md:scale-[1]',
    },
    {
      type: 'doctify',
      scale: 'scale-[0.8] md:scale-[0.8]',
    },
  ];
}

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function findTargetByIdOrTestId(id: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cleanId = id.trim().replace(/^#/, '');

  if (!cleanId) {
    return null;
  }

  return (
    document.getElementById(cleanId) ??
    document.querySelector<HTMLElement>(
      `[data-testid="${escapeSelectorValue(cleanId)}"]`,
    )
  );
}

function findBookingWidgetTarget(): HTMLElement | null {
  return findTargetByIdOrTestId(BOOKING_WIDGET_TARGET_ID);
}

function findEnquiryFormTarget(): HTMLElement | null {
  for (const id of ENQUIRY_FORM_TARGET_IDS) {
    const target = findTargetByIdOrTestId(id);

    if (target) {
      return target;
    }
  }

  return null;
}

function findEnquireNowTrigger(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const clickableElements = Array.from(
    document.querySelectorAll<HTMLElement>(
      'button, a, [role="button"], [data-testid]',
    ),
  );

  return (
    clickableElements.find((element) => {
      const text = element.textContent
        ?.replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      return text === 'enquire now';
    }) ?? null
  );
}

function scrollToTarget(target: HTMLElement): void {
  const top =
    target.getBoundingClientRect().top + window.scrollY - BOOKING_SCROLL_OFFSET;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth',
  });

  const hadTabIndex = target.hasAttribute('tabindex');

  if (!hadTabIndex) {
    target.setAttribute('tabindex', '-1');
  }

  window.setTimeout(() => {
    target.focus({
      preventScroll: true,
    });

    if (!hadTabIndex) {
      target.removeAttribute('tabindex');
    }
  }, 350);
}

const ServiceHeroSection: React.FC<ServiceHeroSectionProps> = ({
  data,
  onButtonClick,
}) => {
  const manifest = useManifest();
  const router = useRouter();
  const handleHeroButtonClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
      button: ButtonBlock,
      index: number,
    ) => {
      onButtonClick?.(button, index, event);

      if (event.defaultPrevented) return;

      if (button.link) return;

      event.preventDefault();

      const bookingWidgetTarget = findBookingWidgetTarget();

      if (bookingWidgetTarget) {
        scrollToTarget(bookingWidgetTarget);
        return;
      }

      const enquiryFormTarget = findEnquiryFormTarget();

      if (enquiryFormTarget) {
        scrollToTarget(enquiryFormTarget);
        return;
      }

      const enquireNowTrigger = findEnquireNowTrigger();

      if (enquireNowTrigger) {
        enquireNowTrigger.click();
        return;
      }

      router.push(BOOKING_FALLBACK_PATH);
    },
    [onButtonClick, router],
  );

  if (!data) {
    return null;
  }

  const {
    heading,
    description,
    buttons,
    link,
    badge,
    trustLogos,
    textsContentData = [],
    images = [],
    bgImageDesktop,
    bgImageMobile,
  } = data;

  const trustLogosToRender: TrustLogoItem[] =
    trustLogos === undefined
      ? getDefaultTrustLogos(manifest)
      : trustLogos.map((logo) => ({
          type: 'image' as const,
          content: logo.content,
          styles: logo.styles,
        }));

  const mobileCollage = <HeroCollage images={images} isDesktop={false} />;

  const desktopCollage = <HeroCollage images={images} isDesktop />;

  const desktopDescription = description
    ? {
        ...description,
        content: (
          <HeroDescription
            content={description.content}
            styles={description.styles}
            bgImageDesktop={bgImageDesktop}
            isDesktop
          />
        ) as React.ReactNode,
      }
    : undefined;

  return (
    <section
      id="service-hero-section"
      data-testid="service-hero-section"
      className="no-scroll-reveal h-auto w-full"
    >
      {/* Mobile hero */}
      <section
        data-testid="service-hero-section-mobile"
        className="no-scroll-reveal relative flex h-auto w-full flex-col justify-center overflow-x-clip overflow-y-visible bg-white min-[820px]:hidden lg:hidden"
        data-header-dark={bgImageMobile ? 'true' : undefined}
      >
        {bgImageMobile ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${getAssetForWidth(manifest, bgImageMobile, 768)})`,
              }}
            />

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-black/30"
            />
          </>
        ) : (
          <>
            <HeroGradientBlobs />

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-linear-to-b from-white to-transparent"
            />
          </>
        )}

        <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col gap-2 px-6 pt-16 pb-10">
          <div className="flex flex-row items-center gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {heading?.content && (
                <div
                  data-testid="hero-heading-mobile"
                  className="mt-10 w-full max-w-lg lg:mt-0 lg:max-w-[813px]"
                >
                  <RichText
                    styles={{
                      ...heading.styles,
                      h1: `text-[28px] font-bold leading-[36px] tracking-[-0.02em] text-text-headings capitalize ${
                        heading.styles?.h1 ?? ''
                      }`,
                    }}
                  >
                    {heading.content}
                  </RichText>
                </div>
              )}

              {description?.content && (
                <HeroDescription
                  content={description.content}
                  styles={description.styles}
                  bgImageMobile={bgImageMobile}
                  isDesktop={false}
                />
              )}
            </div>

            {mobileCollage}
          </div>

          {textsContentData.length > 0 && (
            <div data-testid="hero-feature-cards-mobile" className="w-full">
              <BannerFeatureCards cards={textsContentData} variant="chips" />
            </div>
          )}

          {buttons && buttons.length > 0 && (
            <div
              data-testid="hero-buttons-mobile"
              className="flex w-full flex-row flex-wrap justify-start gap-3"
            >
              {buttons.map((button, index) => (
                <PrimaryButton
                  key={`mobile-hero-button-${button.content ?? index}`}
                  data={button}
                  widthFitContent
                  className="whitespace-nowrap"
                  paddingClassName="px-6 py-3"
                  onClick={(event) =>
                    handleHeroButtonClick(
                      event as React.MouseEvent<
                        HTMLButtonElement | HTMLAnchorElement
                      >,
                      button,
                      index,
                    )
                  }
                />
              ))}
            </div>
          )}

          {badge?.content && (
            <HeroBadge badge={badge} testId="hero-badge-mobile" />
          )}

          {trustLogosToRender.length > 0 && (
            <div
              data-testid="hero-trust-logos-mobile"
              className="flex w-full flex-row flex-nowrap items-center justify-center gap-1.5 overflow-visible py-2"
            >
              {trustLogosToRender.map((logo, index) => {
                if ('type' in logo && logo.type === 'trustpilot') {
                  return (
                    <div
                      key={`trustpilot-${index}`}
                      className="flex h-[110px] w-[110px] shrink-0 origin-center scale-[0.75] items-center justify-center"
                    >
                      <ClinicTrustPilotWidget scale={logo.scale} />
                    </div>
                  );
                }

                if ('type' in logo && logo.type === 'doctify') {
                  return (
                    <div
                      key={`doctify-${index}`}
                      className="relative flex h-[110px] w-[105px] shrink-0 origin-center -translate-y-1 scale-[0.65] items-center justify-center"
                    >
                      <ClinicDoctifyWidget scale={logo.scale} />
                    </div>
                  );
                }

                return (
                  <div
                    key={`logo-${index}`}
                    className="flex h-[110px] w-[100px] shrink-0 origin-center scale-[0.95] items-center justify-center"
                  >
                    <RichText
                      styles={{
                        a: 'flex h-full w-full items-center justify-center',
                        img: 'mt-4 h-auto max-h-[48px] w-auto max-w-full object-contain',
                        ...logo.styles,
                      }}
                    >
                      {logo.content}
                    </RichText>
                  </div>
                );
              })}
            </div>
          )}

          {link?.content && (
            <div data-testid="hero-link-mobile">
              <RichText
                styles={{
                  a: 'font-mulish text-body-lg text-theme-primary underline underline-offset-2 transition-colors duration-200 hover:text-theme-primary-hover',
                  ...link.styles,
                }}
              >
                {link.content}
              </RichText>
            </div>
          )}
        </div>
      </section>

      {/* Tablet and desktop hero */}
      <div className="hidden min-[820px]:block lg:block">
        <BaseHeroSection
          testId="service-hero-section-desktop"
          heading={heading}
          description={desktopDescription}
          buttons={buttons}
          link={link}
          badge={badge}
          trustLogos={trustLogos}
          textsContentData={textsContentData}
          horizontalContentAlignment="left"
          sideContent={desktopCollage}
          backgroundType="image"
          bgImageDesktop={bgImageDesktop}
          bgImageMobile={bgImageMobile}
          onButtonClick={onButtonClick}
        />
      </div>
    </section>
  );
};

export default ServiceHeroSection;
